import { autoinject, transient, computedFrom, BindingEngine } from "aurelia-framework";
import { Router, RouterEvent } from "aurelia-router";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { CfrDocuments } from "./tabs/cfr-documents";
import { CfrOverview } from "./tabs/cfr-overview";
import { StepModel } from "../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { LookupValue, LookupValueRequest } from "../../../../models/lookups/lookup.models";
import { Reflection } from "../../../../utilities/reflection";
import * as moment from "moment";
import { AddSubtaskRequest } from "../../../../models/vetting/vetting.models";
import { CacheLocation, CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { Claims } from "../../../../constants/authentication/claims";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { CfrClientModel, CfrStatusEnum } from "../../../../models/cfr/cfr.models";
import { Assignment } from "../../../components/assignment/assignment";
import { MenuItem } from "../../../components/menu/menu-item";
import { BaseValidationResponse } from "../../../../models/validations/validations.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { CfrService } from "../../../../services/cfr/cfr.service";
import { Correspondence } from "../common-steps/correspondence/correspondence";
import { EntityComments } from "../../common/entity-notes/entity-notes";
import { TitleWithCountHelper } from "../../../../helpers/title-with-count-helper";
import { EmailHistory } from "../common-steps/email-history/email-history";
import { RatingBaseEntity } from "../common-steps/base-rating-entity";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { Guid } from "../../../../utilities/guid";
import { CompCorrespondenceModel } from "../../../../models/vessels/vessel.models";

type CfrSource = 'vessels' | 'activities';
@autoinject
@transient()
export class Cfr extends RatingBaseEntity<CfrClientModel> {

    public shouldRemoveCacheOnDetached: boolean = true;
    public readonly: boolean = true;
    public canAddCFR: boolean;
    public canEditCFR: boolean;
    public canViewCfrDocuments: boolean;
    public canViewCfrResponses: boolean;
    public cfrStatuses: LookupValue[];
    public canChangeCfrStatus: boolean;
    public canAssignCfrRequest: boolean;
    public reopeningCfr: boolean;
    public businessUnitId: number;
    public canViewCfrCorrespondence: boolean;
    public canViewCfrEmailHistory: boolean;
    public assignment: Assignment;

    @computedFrom(Reflection.getPropertyPath<Cfr>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const source: CfrSource = this._params.source;

        switch (source) {
            case 'vessels':
                return `${this.i18N.tr('vessel-text')}`;
            case 'activities':
                return `${this.i18N.tr('tasks-text')}`;
            default:
                {
                    const prevInstruction = this.router.currentInstruction.previousInstruction;
                    return prevInstruction
                        ? prevInstruction.config.title
                        : this.i18N.tr('back-text');
                }
        }
    }


    @computedFrom(Reflection.getPropertyPath<Cfr>(z => z.item.fbrItemId))
    get isLinkedToFbr() {
        return this.item && this.item.fbrItemId && this.item.fbrItemId > 0;
    }

    @computedFrom(Reflection.getPropertyPath<Cfr>(x => x.item), Reflection.getPropertyPath<Cfr>(x => x.item.built))
    public get age() {
        if (this.item) {
            if (this.item.built) {
                return moment().diff(this.item.built, 'year', true).toFixed(1) + "y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    public async canDeactivate(): Promise<boolean> {
        const rfis = await this.cacheClient.get<{ [index: number]: AddSubtaskRequest }>(`RFI-CFR-${this.item.cfrId}`, CacheLocation.SessionStorage, false);
        if (rfis) {
            const result = await new Promise<boolean>((resolve) => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AreYouSureExitDialog, {
                    yes: () => {
                        resolve(true);
                    },
                    no: () => {
                        resolve(false);
                    }
                },
                    true, false, false, 'right');

            });
            return result;
        }

        return true;
    }

    @computedFrom(Reflection.getPropertyPath<Cfr>(v => v.item.vesselId))
    public get vesselUrl() {
        return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
            id: this.item.vesselId,
        });
    }


    constructor(protected router: Router,
        protected bindingEngine: BindingEngine, private dialogClientService: DialogClientService,
        protected i18N: I18N, private userClaimsService: UserClaimsService,
        protected notificationService: NotificationClientService,
        protected eventAggregator: EventAggregator,
        protected cfrOverview: CfrOverview,
        protected cfrDocuments: CfrDocuments,
        protected entityComments: EntityComments,
        protected correspondence: Correspondence,
        protected emailHistory: EmailHistory,
        protected signalClientService: SignalClientService,
        protected validationsClientService: ValidationsClientService,
        protected userClientService: UserClientService,
        protected manageVesselService: ManageVesselService,
        private cacheClient: CacheClientService,
        private cfrService: CfrService,
        private customerService: CommonCustomerService,
        lookupClientService: LookupClientService,
        private activeMenuItemsService: ActiveMenuItemsService,
        private clipboardCopyService: ClipboardCopyService) {
        super(router, bindingEngine, i18N, notificationService, eventAggregator, validationsClientService, signalClientService, manageVesselService);
        this.cfrOverview.assignToMe = this.assignToMe;
        this.canAddCFR = this.userClaimsService.hasPermission(Claims.CanAddCFR);
        this.canEditCFR = this.userClaimsService.hasPermission(Claims.CanEditCFR);
        this.canViewCfrDocuments = this.userClaimsService.hasPermission(Claims.CanViewCfrDocuments);
        this.canChangeCfrStatus = this.userClaimsService.hasPermission(Claims.CanChangeCfrStatus);
        this.canAssignCfrRequest = this.userClaimsService.hasPermission(Claims.CanAssignCfrRequest);
        this.reopeningCfr = this.userClaimsService.hasPermission(Claims.ReopeningCFR);
        this.canViewCfrCorrespondence = this.userClaimsService.hasPermission(Claims.CanViewCfrCorrespondence);
        this.canViewCfrEmailHistory = this.userClaimsService.hasPermission(Claims.CanViewCfrEmailHistory);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.CfrStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.cfrStatuses = x.activeValues;
        });

        this.customerService.getBusinessUnitId().then(x => {
            this.businessUnitId = x;
        });

        eventAggregator.subscribe(RouterEvent.Processing, (x) => {
            if (x && x.instruction && x.instruction.config) {
                const name = x.instruction.config.name;
                this.shouldRemoveCacheOnDetached = name != RightShipQiRouterConstants.RouteNames.PSC && name != RightShipQiRouterConstants.RouteNames.Incident;
            }
        });
    }


    @computedFrom("item")
    get menuItems(): MenuItem[] {
        const visible = this.item ? true : false;
        return [
            {
                click: () => this.editCfr(),
                isVisible: this.canEditCFR,
                item: {
                    addicon: false,
                    editicon: true,
                    removeicon: false,
                    text: 'Edit'
                },
                classList: '',
            },
            {
                click: () => {
                    this.activateCorrespondence();
                    this.correspondence.addSubtask(false)
                },
                isVisible: visible,
                item: {
                    addicon: true,
                    editicon: false,
                    removeicon: false,
                    text: 'Create RFI'
                },
                classList: ''
            },
            {
                click: () => {
                    this.activateCorrespondence();
                    this.correspondence.addSubtask(true)
                },
                isVisible: visible && this.userClaimsService.hasPermission(Claims.CanCreateInternalRfi),
                item: {
                    addicon: true,
                    editicon: false,
                    removeicon: false,
                    text: 'Create Internal RFI'
                },
                classList: ''
            }
        ].filter(x => x.isVisible);
    }

    activateCorrespondence() {
        if (!this.correspondence.correspondenceEntity) {
            this.correspondence.activate(this.getCorrespondenceModel());
        }
    }

    private getCorrespondenceModel(): CompCorrespondenceModel {
        return {
            correspondenceEntity: {
                entityId: this.item.cfrId,
                entityTypeId: this.item.entityType,
                isInconsistentState: false,
                requestedBy: this.item.requestedBy,
                liaseWithShipManagers: null,
                assignment: {
                    assignedTo: this.item.assignedUserId,
                    assignedToUserName: this.item.assignedTo,
                    assignToMe: this.assignToMe.bind(this),
                    isAssignedToMe: () => this.item.assignedUserId == this.userClientService.currentUser.id,
                }
            },
            name: "CFR",
            showAddRFI: this.item && !this.statusIsFinal,
            hasOutcome: this.statusIsFinal,
            emails: [{ email: this.item.requestedByEmail }],
            newDocumentAdded: () => {
                this.item.documentsCount += 1;
            }
        }
    }

    editCfr() {
        if (this.item.assignedUserId == this.userClientService.currentUser.id) {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.NewCfr, {
                vesselId: this.item.vesselId,
                cfrId: this.item.cfrId
            });
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                name: "Condition for Review",
                submit: this.assignToMe,
                callback: this.editCfr.bind(this),
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    @computedFrom(Reflection.getPropertyPath<Cfr>(v => v.item), Reflection.getPropertyPath<Cfr>(v => v.item.cfrStatusId))
    public get statusIsFinal(): boolean {
        return this.item &&
            (this.item.cfrStatusId == CfrStatusEnum.Closed || this.item.cfrStatusId == CfrStatusEnum.Cancelled);
    }

    assignToMe = async () => {
        if (this.assignment) {
            await this.assignment.assignToMe();
            this.item.cfrStatusId = CfrStatusEnum.InProgress;
            this.item.assignedUserId = this.userClientService.currentUser.id;
        }
    }

    protected onActivation() {
        this.activeMenuItemsService.setActiveFromMenuItem(this.i18N.tr('tasks-text'));
    }

    protected getListRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Activities;
    }



    protected getSteps(): StepModel[] {
        return [
            {
                lazyLoad: true,
                title: this.i18N.tr("cfr-overview-text"),
                step: 1,
                vm: this.cfrOverview,
                getModel: () => { return { item: this.item, assignment: this.assignment } },
                show: true
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle(this.i18N.tr("documents-text"), this.item.documentsCount),
                step: 2,
                vm: this.cfrDocuments,
                show: this.canViewCfrDocuments
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Private Notes", this.item.privateNotesCount),
                show: this.userClaimsService.hasPermission(Claims.CanViewCFRNotes),
                step: 3,
                vm: this.entityComments,
                getModel: () => ({
                    canSubmit: true,
                    entityTypeId: this.item.entityType,
                    entityId: this.item.cfrId,
                    item: this.item
                })
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle("Correspondences", this.correspondence.correspondenceCount ?? this.item.correspondenceCount),
                step: 4,
                vm: this.correspondence,
                getModel: () => {
                    return this.getCorrespondenceModel();
                },
                show: () => this.canViewCfrCorrespondence && this.item ? true : false
            },
            {
                lazyLoad: true,
                title: "Email History",
                step: 5,
                vm: this.emailHistory,
                getModel: () => {
                    return {
                        item: this.item
                    }
                },
                show: () => this.canViewCfrEmailHistory && this.item ? true : false
            }
        ]
    }


    protected getItem(id: number): Promise<CfrClientModel> {
        return this.cfrService.getCfr(id);
    }

    protected getItemId(): number {
        return this.item ? this.item.cfrId : null;
    }

    protected saveItem(): Promise<BaseValidationResponse> {
        return null;
    }

    protected getUpdateRoute(): string {
        return RightShipQiRouterConstants.RouteNames.Cfr;
    }

    protected getEntityType(): EntityTypes {
        return EntityTypes.Cfr;
    }

    protected initializeItemForCreate(): CfrClientModel | Promise<CfrClientModel> {
        const cfr: CfrClientModel = {
            title: null,
            phoneNumbers: [],
            comments: null,
            cfrStatusId: null,
            cfrStatus: null,
            vesselId: null,
            cfrId: null,
            yearOfConstruction: null,
            dateRequested: null,
            built: null,
            businessUnitId: null,
            deadWeightTonnage: null,
            requestedBy: null,
            assignedUserId: null,
            internationalMaritimeOrganizationNumber: null,
            europeanNumberOfIdentification: null,
            vehicleIdentificationNumber: null,
            vesselName: null,
            vesselType: null,
            cfrStatusHistory: [],
            rating: null,
            starRating: null,
            cfrCategoryId: null,
            cfrCategory: null,
            cfrVettingCondition: null,
            customerId: null,
            description: null,
            expiryDate: null,
            reviewRequired: null,
            cfrCorrespondenceEmails: [],
            identifier: Guid.generateGuid()
        }
        return cfr;
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    openCfrStatusDialog() {
        if (this.canChangeCfrStatus) {
            if (!this.statusIsFinal || (this.reopeningCfr && this.statusIsFinal)) {
                if (this.item.assignedUserId == this.userClientService.currentUser.id || this.statusIsFinal) {
                    const reloadItemFunc = () => this.getItem(this.getItemId()).then(x => {
                        this.item = x;
                        setTimeout(() => {
                            this.refreshSteps();
                        });
                    });

                    this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrStatusDialog,
                        {
                            formName: this.item.title,
                            otherComments: this.item.comments,
                            cfrId: this.item.cfrId,
                            reload: (() => {
                                this.getItem(this.getItemId()).then(x => {
                                    this.item = x;
                                    this.refreshSteps();
                                });
                            }),
                            cfrStatusId: this.item.cfrStatusId,
                            onDraftGenerated: reloadItemFunc,
                            onSubmit: reloadItemFunc,
                            vesselName: this.item.vesselName,
                            internationalMaritimeOrganizationNumber: this.item.internationalMaritimeOrganizationNumber,
                            europeanNumberOfIdentification: this.item.europeanNumberOfIdentification,
                            vehicleIdentificationNumber: this.item.vehicleIdentificationNumber
                        }, true, false, false, 'right');
                } else {
                    this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CfrAssignmentDialog, {
                        name: "Condition for Review",
                        submit: this.assignToMe,
                        callback: this.openCfrStatusDialog.bind(this),
                        currentAssignment: this.assignment.assignedEntity.assignedToUserName
                    }, true, false, false);
                }
            }
            else if (this.statusIsFinal && !this.reopeningCfr) {
                this.notificationService.show("You do not have permission to reopen a closed CFR", "", NotificationType.Warning);
            }

        }
    }

    onAttached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Cfr), this.refreshCfrStatus);
    }

    onDetached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Cfr), this.refreshCfrStatus);
    }

    refreshCfrStatus = (param: EntityStatusChangeModel) => {

        if (!this.item) return;
        
        if (this.item.cfrStatusId == CfrStatusEnum.Closed) return;
        
        if (this.item.cfrId == param.entityId) {

            if (this.assignment && param.statusId != CfrStatusEnum.CfrReassignment) {
                this.assignment.reload().then(x => this.item.assignedUserId = x.assignedTo);
            }

            const vetStatus = this.cfrStatuses.filter(x => x.id == param.statusId)[0];

            if (vetStatus) {
                this.item.cfrStatus = vetStatus.name;
                this.item.cfrStatusId = vetStatus.id;
                this.item.cfrStatusHistory.splice(0, 0, {
                    previousStatusId: this.item.cfrStatusHistory[0] ? this.item.cfrStatusHistory[0].statusId : null,
                    createdDate: new Date(),
                    status: vetStatus.name,
                    statusId: vetStatus.id,
                    user: param.userName ? param.userName : "",
                    subtaskTypes: [],
                    comment: param.statusSnippet,
                    fullComment: param.comment
                });
            }
        }
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }
}